import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { SOLUTION_CATEGORIES } from '../data/homeMenus';

const INTROS = {
  'home-property-finance': 'Financing structures built around residential and commercial property — from acquisition and construction to unlocking capital against owned assets.',
  'business-corporate-finance': 'Debt and growth capital for operating businesses — working capital, term debt, syndication and project financing structured around cash flows.',
  'trade-supply-chain-finance': 'Structures that release liquidity locked in trade cycles — receivables, payables, letters of credit and guarantees.',
  'structured-capital-markets': 'Structured credit and capital market instruments for requirements that conventional lending does not fully address.',
  'asset-backed-lending': 'Liquidity against financial assets — shares, mutual funds, deposits and bullion — without liquidating the underlying investment.',
  'equipment-machinery-finance': 'Acquisition, refinance and leasing structures for plant, machinery and specialised equipment.',
  'professional-education-finance': 'Financing tailored to practising professionals and education-linked requirements.',
  'specialized-funding-solutions': 'Purpose-built funding routes including government-backed guarantee schemes and specialised assessments.'
};

const SolutionCategoryPage = () => {
  const { categorySlug } = useParams();
  const category = SOLUTION_CATEGORIES.find((c) => c.slug === categorySlug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} — Capital Solutions | LOANYARD CAPITAL`;
    }
  }, [category]);

  if (!category) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen">
      <Header />
      <WhatsAppButton />

      <div className="bg-white pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-500 mb-4 sm:mb-6">
              <Link to="/" className="hover:text-emerald-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <a href="/#capital-solutions" className="hover:text-emerald-600 transition-colors">Capital Solutions</a>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-slate-700 font-medium">{category.name}</span>
            </nav>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 leading-[1.1]">
              {category.name.split(' ').slice(0, -1).join(' ')} <span className="text-emerald-600">{category.name.split(' ').slice(-1)}</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-10 sm:mb-12 max-w-3xl">{INTROS[category.slug]}</p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-solutions-grid">
              {category.items.map(([label, to]) => (
                <Link
                  key={to}
                  to={to}
                  className="group flex items-center justify-between bg-white rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all duration-300 px-6 py-5"
                  data-testid={`category-solution-${to.replace('/', '')}`}
                >
                  <span className="font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">{label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 flex-shrink-0 transition-colors" />
                </Link>
              ))}
            </div>

            <div className="mt-14 p-8 bg-emerald-50 rounded-2xl border border-emerald-200 text-center">
              <p className="text-lg text-slate-700 mb-4 font-medium">Not sure which structure fits your requirement?</p>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
              >
                Discuss Your Requirement <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SolutionCategoryPage;
